/**
 * In-memory store for camera thumbnail timestamps and aspect ratios.
 * Used to cache the last known thumbnail time for each camera to provide instant fallback
 * when loading new thumbnails on slow connections.
 *
 * This is a module-level store (not React state) because:
 * - Updates happen multiple times per second (performance critical)
 * - Data doesn't need to trigger React re-renders
 * - Only needs to persist during navigation
*/

type ThumbsRecord = Record<string, number>;

const MAX_THUMBS = 10000; // Safety limit to prevent memory leaks in edge cases
let thumbs: ThumbsRecord = {};
// A camera's thumb resolution doesn't change between frames, so the aspect ratio measured
// from one thumb is valid for the next. Caching it means we know how to lay a thumb out
// on the first render it appears in, rather than after it has loaded.
let aspectRatios: ThumbsRecord = {};

/** Drop aspect ratios for cameras we no longer hold a thumb time for. */
const pruneAspectRatios = (): void => {
    aspectRatios = Object.fromEntries(
        Object.entries(aspectRatios).filter(([uidd]) => uidd in thumbs)
    );
};

/**
 * Store the last thumbnail timestamp for a camera.
 * Automatically prunes oldest entries if MAX_THUMBS limit is exceeded.
 * @param {string} uidd Camera unique identifier (format: "uid.deviceId").
 * @param {number} time Timestamp in seconds when thumbnail was captured.
 */
export const setCameraLastThumbTime = (uidd: string, time: number): void => {
    thumbs[uidd] = time;

    const cameraCount = Object.keys(thumbs).length;
    if (cameraCount > MAX_THUMBS) {
        // Remove oldest entries - sort by timestamp descending and keep newest
        const entries = Object.entries(thumbs).sort(([, a], [, b]) => b - a);
        thumbs = Object.fromEntries(entries.slice(0, MAX_THUMBS));
        pruneAspectRatios();
    }
};

/**
 * Store the aspect ratio of a camera's most recently loaded thumbnail.
 * @param {string} uidd Camera unique identifier.
 * @param {number} aspectRatio Thumbnail width / height.
 */
export const setCameraThumbAspectRatio = (uidd: string, aspectRatio: number): void => {
    aspectRatios[uidd] = aspectRatio;

    if (Object.keys(aspectRatios).length > MAX_THUMBS) {
        pruneAspectRatios();
    }
};

/**
 * Get the aspect ratio of a camera's most recently loaded thumbnail.
 * @param {string} uidd Camera unique identifier.
 * @returns {number|undefined} Thumbnail width / height, or undefined if none has loaded yet.
 */
export const getCameraThumbAspectRatio = (uidd: string): number | undefined => {
    return aspectRatios[uidd];
};

/**
 * Get the last known thumbnail timestamp for a camera.
 * @param {string} uidd Camera unique identifier.
 * @returns {number|null} Timestamp in seconds, or null if no thumbnail exists.
 */
export const getCameraLastThumbTime = (uidd: string): number | null => {
    return thumbs[uidd] || null;
};

/**
 * Clear all stored thumbnail timestamps and aspect ratios.
 * Should be called on logout to prevent data leaking between users / reaching size limit
 */
export const clearCameraThumbTimes = (): void => {
    thumbs = {};
    aspectRatios = {};
};